import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import BreadcrumbNav from '@/components/BreadcrumbNav'
import CoverProduct from '@/components/CoverProduct'
import YoutubeVideo from '@/components/YoutubeVideo'
import Carousel from '@/components/Carousel'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { removeBr } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const Service = ({
  cpt: cptFromProps,
  stub: stubFromProps,
  tax: taxFromProps,
}) => {
  const { getImageUrl, headerHeight, setPageAnimationReady } = useContext(GeneralContext)
  const classes = useStyles({ headerHeight })
  const preload = useRef(new CreateJs.LoadQueue())
  const { stub: stubFromParams } = useParams()
  const stub = useRef(stubFromParams || stubFromProps)
  const [load, setLoad] = useState(false)
  const [currentCategory, setCurrentCategory] = useState(false)
  const $root = useRef()
  const gallery = useRef([])

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, categories } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    strings: state.language.string,
    categories: state.page.taxonomies[taxFromProps] || [],
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getSingleProduct = useCallback((cpt, index) => dispatch(actions.page.getDataCPT(cpt, index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub.current, 'pages')
    preload.current.loadManifest(heroMedia)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = () => {
    if (page.project_gallery_id) {
      const gal = page.project_gallery_id.split(',')
      const imgSize = 'full'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub.current, 'pages')
          const ratio = height / width

          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      gallery.current = galleryItems[0] ? galleryItems : []
    }
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
      setGallery()
    }
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    getSingleProduct(cptFromProps, stub.current)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  useEffect(() => {
    if (
      Object.keys(page).length > 0
      && categories.length > 0
    ) {
      setCurrentCategory(categories.find((cat) => cat.id === page[taxFromProps][0]))
    }
  }, [categories, page])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Breadcrumb
  ------------------------------*/
  const renderBreadcrumb = () => {
    return (
      <div className={classes.wrapper}>
        <BreadcrumbNav
          items={[
            {
              id: currentCategory.id,
              title: currentCategory.name,
              slug: currentCategory.slug,
              link: currentCategory.link,
            },
            {
              id: page.id,
              title: removeBr(page.title.rendered),
              slug: page.slug,
              link: page.link,
            },
          ]}
          back
        />
      </div>
    )
  }

  /*------------------------------
  Render Cover Product
  ------------------------------*/
  const renderCover = () => {
    return (
      <div className={`${classes.wrapper} ${classes.coverProduct}`}>
        <CoverProduct
          pretitle={currentCategory.name}
          title={page.title.rendered}
          text={page.excerpt.rendered}
          img={getImageUrl(page.featured_media, 'full', stub.current)}
        />
      </div>
    )
  }

  /*------------------------------
  Render Text
  ------------------------------*/
  const renderText = () => {
    return page.content.rendered && (
      <div className={`${classes.wrapper} ${classes.description}`}>
        <div
          className={classes.text}
          dangerouslySetInnerHTML={{ __html: page.content.rendered }}
        />
      </div>
    )
  }

  /*------------------------------
  Render Video
  ------------------------------*/
  const renderVideo = () => {
    return page.video_content_url && (
      <div className={`${classes.wrapper} ${classes.video}`}>
        <YoutubeVideo
          videoId={page.video_content_url}
          poster={page.video_content_image || null}
        />
      </div>
    )
  }

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGalleryProduct = () => {
    return gallery.current.length > 1 && (
      <div className={`${classes.wrapper} ${classes.gallery}`}>
        <Carousel
          imagesList={gallery.current}
          parallax
          dots
          arrows
        />
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer storeLocator />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderBreadcrumb()}
        {renderCover()}
        {renderText()}
        {renderVideo()}
        {renderGalleryProduct()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageService ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Service
